import { PopupDialog } from '@appcharge/shared-ui';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { useEffect, useState } from 'react';
import LanguageIcon from '@mui/icons-material/Language';
import { i18n, changeLanguage } from '../../i18n';
import { DEFAULT_SYSTEM_LANGUAGE } from '../../constants/defaults';
import { useLocalizationState } from 'state/hooks/localization.state.hook';
import { useTranslation } from 'react-i18next';
import useCustomEvents from 'hooks/useCustomEvents';
import { EEventsType, ELocalStorageKeys, EStorePhase } from 'constants/enums';
import { webStorageUtil } from 'state/webStorage.state.service';
import useApi from 'hooks/useApi';
import { BootResponse } from '../../constants/apiResponses.types';
import './style.scss';

const LanguagePicker = ({ fontSize = 16 }: { fontSize?: number }) => {
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const { currentLanguage } = useLocalizationState();
  const customEvents = useCustomEvents();
  const API = useApi({});
  const publisherMetaData = API.getPublisherMeta.data as BootResponse;
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>([DEFAULT_SYSTEM_LANGUAGE]);
  const { t } = useTranslation();
  const parseLngName = (lang: string) => (lang === 'en' ? DEFAULT_SYSTEM_LANGUAGE : lang);

  useEffect(() => {
    const updateSupportedLanguages = () => {
      const languages = Array.isArray(i18n.options.supportedLngs)
        ? i18n.options.supportedLngs.filter((lng) => lng !== 'cimode')
        : [DEFAULT_SYSTEM_LANGUAGE];
      if (languages.length > 0) {
        setSupportedLanguages(languages);
      }
    };
    updateSupportedLanguages();
    i18n.on('supportedLanguagesChanged', updateSupportedLanguages);

    return () => {
      i18n.on('supportedLanguagesChanged', updateSupportedLanguages);
    };
  }, []);

  const handleOnClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const language = event.currentTarget.getAttribute('data-language');
    if (language) {
      await changeLanguage(language);
      setIsLanguageModalOpen(false);
      customEvents.sendCustomEvent(
        EEventsType.LANGUAGE_SELECTED,
        {},
        webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN) !== null
          ? EStorePhase.POST_LOGIN
          : EStorePhase.PRE_LOGIN
      );
    }
  };

  return (
    <>
      <PopupDialog
        open={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
        ariaLabel={t('changeLanguage')}
        className="language-picker-modal"
      >
        <div
          style={{
            width: 400,
            maxWidth: '90vw',
            backgroundColor: 'white',
            borderRadius: 8,
            boxShadow: '0px 11px 15px rgba(0,0,0,0.2), 0px 9px 46px rgba(0,0,0,0.12)',
            padding: 16,
            fontFamily: publisherMetaData?.storeTheme?.general?.font,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, fontFamily: 'inherit' }}>
              {t('changeLanguage')}
            </h2>
            <button
              type="button"
              aria-label="close"
              className="language-picker-close-btn"
              onClick={() => setIsLanguageModalOpen(false)}
            >
              <CloseIcon />
            </button>
          </div>
          <hr style={{ border: 'none', borderTop: '1px solid black', margin: 0 }} />
          <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0 }}>
            {supportedLanguages.map((lang, index) => (
              <li key={index} style={{ marginBottom: index === supportedLanguages.length - 1 ? 0 : '12px' }}>
                <button
                  type="button"
                  className="language-picker-option"
                  data-testid={`select-${lang}-language-btn`}
                  data-language={lang}
                  onClick={handleOnClick}
                >
                  <CheckIcon
                    style={{
                      color: '#000',
                      marginRight: 4,
                      visibility: currentLanguage === lang ? 'visible' : 'hidden',
                    }}
                  />
                  <span
                    style={{
                      color: currentLanguage === lang ? '#000' : '#999999',
                      fontSize: '1rem',
                      fontWeight: currentLanguage === lang ? 600 : 500,
                      fontFamily: 'inherit',
                    }}
                  >
                    {lang}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </PopupDialog>
      <button
        type="button"
        className="language-picker-trigger"
        data-testid="language-modal-open-btn"
        onClick={() => setIsLanguageModalOpen(true)}
        style={{
          color: 'white',
          fontSize: `${fontSize}px`,
          fontWeight: 'normal',
          fontFamily: 'inherit',
        }}
      >
        <LanguageIcon style={{ width: '18px' }} />
        {parseLngName(i18n.language)}
      </button>
    </>
  );
};

export default LanguagePicker;
